import React, { useMemo, useRef, useState } from 'react';

export interface SearchSelectOption {
  id: string;
  label: string;
  sublabel?: string;
}

interface SearchSelectProps {
  label?: string;
  required?: boolean;
  placeholder?: string;
  options: SearchSelectOption[];
  value: string | null;
  onChange: (id: string | null) => void;
  disabled?: boolean;
  emptyText?: string;
  /** Max options rendered in the dropdown while typing. */
  maxResults?: number;
  className?: string;
  id?: string;
}

/**
 * A searchable / filterable single-select input. When nothing is chosen it
 * renders a type-to-filter text box with a dropdown; once a value is picked it
 * collapses to a removable chip. Self-contained: the parent only owns the
 * selected id via `value` / `onChange`.
 *
 * Styling mirrors the app's existing DealCombobox (avcs palette + Tailwind).
 */
const SearchSelect: React.FC<SearchSelectProps> = ({
  label,
  required = false,
  placeholder = 'Type to search…',
  options,
  value,
  onChange,
  disabled = false,
  emptyText = 'No matches found.',
  maxResults = 12,
  className = '',
  id,
}) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const selected = useMemo(
    () => options.find((o) => o.id === value) || null,
    [options, value],
  );

  const results = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const filtered = needle
      ? options.filter(
          (o) =>
            o.label.toLowerCase().includes(needle) ||
            (o.sublabel ? o.sublabel.toLowerCase().includes(needle) : false),
        )
      : options;
    return filtered.slice(0, maxResults);
  }, [options, search, maxResults]);

  function handleBlur() {
    // Delay so a mousedown on an option can register before the dropdown closes.
    blurTimer.current = setTimeout(() => setOpen(false), 120);
  }

  function handleSelect(optId: string) {
    if (blurTimer.current) clearTimeout(blurTimer.current);
    onChange(optId);
    setSearch('');
    setOpen(false);
  }

  return (
    <div className={`grid gap-1 text-sm text-avcs-text ${className}`}>
      {label && (
        <span className="font-medium">
          {label} {required && <span className="text-[#b42318]">*</span>}
        </span>
      )}
      {selected ? (
        <div className="flex items-center gap-2 rounded-lg border border-[#1e6bb833] bg-[#edf5ff] px-3 py-2">
          <span className="flex-1 truncate font-medium text-[#0f3b64]" title={selected.label}>
            {selected.label}
            {selected.sublabel && (
              <span className="ml-2 text-xs font-normal text-avcs-muted">{selected.sublabel}</span>
            )}
          </span>
          {!disabled && (
            <button
              type="button"
              className="text-avcs-muted hover:text-[#6b1010] text-base leading-none"
              onClick={() => onChange(null)}
              aria-label={`Clear ${label || 'selection'}`}
            >
              ×
            </button>
          )}
        </div>
      ) : (
        <div className="relative">
          <input
            id={id}
            className="w-full rounded-lg border border-[#1e6bb833] px-3 py-2 disabled:bg-[#f4f6f9] disabled:text-avcs-muted"
            placeholder={placeholder}
            value={search}
            disabled={disabled}
            onChange={(e) => {
              setSearch(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onBlur={handleBlur}
            autoComplete="off"
            role="combobox"
            aria-expanded={open}
            aria-controls={id ? `${id}-listbox` : undefined}
          />
          {open && !disabled && (
            <div
              id={id ? `${id}-listbox` : undefined}
              role="listbox"
              className="absolute z-30 left-0 right-0 top-full mt-1 max-h-56 overflow-auto rounded-lg border border-[#1e6bb833] bg-white shadow-lg"
            >
              {results.length === 0 ? (
                <div className="px-3 py-2 text-xs text-avcs-muted">{emptyText}</div>
              ) : (
                results.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    role="option"
                    aria-selected={opt.id === value}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-[#edf5ff] text-avcs-text border-b border-[#1e6bb812] last:border-0"
                    onMouseDown={(ev) => ev.preventDefault()}
                    onClick={() => handleSelect(opt.id)}
                  >
                    <span className="font-medium">{opt.label}</span>
                    {opt.sublabel && (
                      <span className="ml-2 text-xs text-avcs-muted">{opt.sublabel}</span>
                    )}
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchSelect;
